import { Injectable, signal } from '@angular/core';

/**
 * One stop on a wayfinding route: the help-path to fly to, plus the model text
 * shown while the wren points at it (e.g. "Click Labor"). `text` is optional —
 * the wren derives a sensible default from the target element when it's omitted.
 */
export interface WrenWayfindHop {
  helpPath: string;
  text?: string;
}

/** A resolved hop the wren can act on: the live element, its current rect, and
 * the prompt text. The wren lands on `rect`, shows `text`, and listens for a
 * click on `el` to advance. */
export interface WrenWayfindTarget {
  helpPath: string;
  el: HTMLElement;
  rect: DOMRect;
  text?: string;
}

// Cross-route resolve window: after an advance() click navigates to another
// route, how long we poll for the next hop's anchor to render before giving up
// (lazy routes + grid hydration on the slower pages fit inside this comfortably).
const PENDING_RESOLVE_TIMEOUT_MS = 4000;
const PENDING_RESOLVE_POLL_MS = 150;

/**
 * Ephemeral wren wayfinding — "fly to this element" WITHOUT a full walkthrough.
 *
 * A caller (e.g. the Ask Ryn 'fly' action: sparta-ryn answering "where is X")
 * calls `flyTo(helpPath)` for a single element, or `flyRoute([...])` for an
 * ordered, click-driven tour across several controls. The docked wren flies to
 * the first hop, points at it, and HOLDS — it does not auto-leave on a timer.
 * When the user clicks the control the wren is pointing at, the wren calls
 * `advance()` to fly to the next hop, or redocks once the route is done. If the
 * first hop isn't rendered, `flyTo`/`flyRoute` return false so the caller can
 * fall back to a text-only answer.
 *
 * Cross-route hops: when the click on an anchor navigates to another route, the
 * next hop's element only exists after the new page renders. `advance()` then
 * enters a bounded pending-resolve (see `pendingResolve`) that polls for the
 * remaining hops and re-fires `target` once one appears — so a route can span
 * pages. If nothing renders inside the window, the route ends quietly.
 *
 * providedIn root so any trigger shares the one instance. Pure state — the wren
 * owns the animation, the click handling, and the redock; this service only
 * resolves hops to on-screen targets and tracks the route position.
 */
@Injectable({ providedIn: 'root' })
export class WrenWayfindingService {
  /** The resolved hop the wren should fly to and hold on, or null for none. */
  readonly target = signal<WrenWayfindTarget | null>(null);

  /** True while advance() is awaiting a cross-route hop to render (bounded —
   * see PENDING_RESOLVE_TIMEOUT_MS). The wren treats a null advance() with
   * pendingResolve set as "release the hold but keep the route": it redocks and
   * lets the next `target` emission fly it to the late-rendering hop. */
  readonly pendingResolve = signal(false);

  private hops: WrenWayfindHop[] = [];
  private index = 0;
  private pollHandle?: number;

  /**
   * Single-element wayfind (the 'fly' action's one helpPath). Back-compat shim
   * over a one-hop route. `text` is optional model copy for the prompt bubble.
   */
  flyTo(helpPath: string, text?: string): boolean {
    return this.flyRoute([{ helpPath, text }]);
  }

  /**
   * Click-driven route: fly to hop 0 and hold; the wren advances on each click
   * (see `advance`), redocking after the last hop. Returns false when hop 0
   * isn't currently rendered (caller falls back to a text-only answer).
   */
  flyRoute(hops: WrenWayfindHop[]): boolean {
    const first = hops?.[0];
    if (!first) {
      return false;
    }
    this.cancelPendingResolve(); // a new route supersedes any awaited hop
    const resolved = this.resolve(first);
    if (!resolved) {
      console.log(
        '[wren-wayfind] hop 0 not rendered, caller falls back to text-only:',
        first.helpPath
      );
      return false;
    }
    this.hops = hops;
    this.index = 0;
    this.target.set(resolved);
    return true;
  }

  /**
   * Advance to the next hop after the user clicks the current anchor. Returns the
   * next resolved target, or null when the route is complete (the wren redocks).
   * Hops whose element isn't currently rendered are skipped (same-page semantics).
   *
   * Cross-route: when hops remain but NONE are rendered — typically because the
   * click just navigated to another route that hasn't rendered yet — this returns
   * null with `pendingResolve` set and polls for the remaining hops, re-firing
   * `target` when one appears (or ending the route on timeout).
   */
  advance(): WrenWayfindTarget | null {
    const from = this.index + 1;
    for (let i = from; i < this.hops.length; i++) {
      const resolved = this.resolve(this.hops[i]);
      if (resolved) {
        this.index = i;
        this.target.set(resolved);
        return resolved;
      }
    }
    if (from < this.hops.length) {
      this.beginPendingResolve(from);
      return null;
    }
    this.clear();
    return null;
  }

  /** Cancel/end a wayfind (the wren returns to its dock). */
  clear(): void {
    this.cancelPendingResolve();
    this.hops = [];
    this.index = 0;
    this.target.set(null);
  }

  /** Poll for the first remaining hop (order preserved) to render after a
   * cross-route navigation, then re-fire `target`. Bounded: ends the route if
   * nothing renders inside the window. setInterval over rAF on purpose — rAF
   * stalls in background tabs, and a wayfind can legitimately span a tab switch. */
  private beginPendingResolve(from: number): void {
    this.cancelPendingResolve();
    if (typeof window === 'undefined') {
      this.clear(); // SSR safety: nothing will ever render
      return;
    }
    this.pendingResolve.set(true);
    const deadline = performance.now() + PENDING_RESOLVE_TIMEOUT_MS;
    this.pollHandle = window.setInterval(() => {
      for (let i = from; i < this.hops.length; i++) {
        const resolved = this.resolve(this.hops[i]);
        if (resolved) {
          this.cancelPendingResolve();
          this.index = i;
          this.target.set(resolved);
          return;
        }
      }
      if (performance.now() >= deadline) {
        console.warn(
          '[wren-wayfind] cross-route hop never rendered, ending route:',
          this.hops[from]?.helpPath
        );
        this.clear();
      }
    }, PENDING_RESOLVE_POLL_MS);
  }

  private cancelPendingResolve(): void {
    if (this.pollHandle !== undefined && typeof window !== 'undefined') {
      window.clearInterval(this.pollHandle);
    }
    this.pollHandle = undefined;
    this.pendingResolve.set(false);
  }

  /** Resolve a hop's help-path to its on-screen element + rect, or null if it
   * isn't currently rendered. Matches by attribute value rather than a CSS
   * selector — help-paths are slash-separated ids awkward to escape in a selector. */
  private resolve(hop: WrenWayfindHop): WrenWayfindTarget | null {
    if (!hop?.helpPath || typeof document === 'undefined') {
      return null;
    }
    const el = Array.from(document.querySelectorAll('[help-path]')).find(
      (e) => e.getAttribute('help-path') === hop.helpPath
    ) as HTMLElement | undefined;
    if (!el) {
      return null;
    }
    return {
      helpPath: hop.helpPath,
      el,
      rect: el.getBoundingClientRect(),
      text: hop.text,
    };
  }
}
